import { Inject, Injectable } from '@angular/core';
import { WhitelabelService } from '@galaxy/partner';
import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

export interface VBCEntryInfo {
  url: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessCenterService {
  private entryUrl$: Observable<string>;
  private vbcName$: Observable<string> = this.partnerId$.pipe(
    switchMap((partnerId) => this.whitelabelService.getBranding(partnerId)),
    map((branding) => branding.apps['VBC'].name),
    shareReplay(1),
  );
  private multiLocationName$: Observable<string>;

  constructor(
    private ssoService: SSOService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private whitelabelService: WhitelabelService,
  ) {
    // Whitelabelled entry URL
    this.entryUrl$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.ssoService.getEntryUrl('VBC', newPartnerServiceContext(partnerId));
      }),
      shareReplay(1),
    );
  }

  getVBCEntryUrl(accountGroupId?: string, page = 'dashboard'): Observable<VBCEntryInfo> {
    if (accountGroupId) {
      // Entry URL with nextUrl to go to location's dashboard
      return combineLatest([this.entryUrl$, this.vbcName$]).pipe(
        map(([entryUrl, vbcName]) => {
          return {
            url: addNextUrl(entryUrl, `/account/location/${accountGroupId}/${page}`),
            name: vbcName,
          };
        }),
      );
    }
    // if no account group chosen, just redirect to /entry, and let business app
    // handle looking up default account group
    return combineLatest([this.entryUrl$, this.vbcName$]).pipe(
      map(([entryUrl, vbcName]) => {
        return {
          url: entryUrl,
          name: vbcName,
        };
      }),
    );
  }

  getMultiLocationEntryUrl(brandName: string): Observable<VBCEntryInfo> {
    // Entry URL with nextUrl to go to location's dashboard
    return combineLatest([this.entryUrl$, this.vbcName$]).pipe(
      map(([entryUrl, name]) => {
        // Manually replace any slash with a '%2f'
        brandName = decodeURIComponent(brandName).replace(/\//g, '%2F');
        return {
          url: addNextUrl(entryUrl, `/account/brands/${brandName}/dashboard`),
          name: `${name}`,
        };
      }),
    );
  }
}

export function addNextUrl(entryUrl: string, nextUrl: string): string {
  if (!('URL' in window)) {
    return entryUrl + `?nextUrl=${encodeURIComponent(nextUrl)}`;
  }
  const urlObj = new URL(entryUrl);
  const params = new URLSearchParams(urlObj.search);
  params.append('nextUrl', nextUrl);
  urlObj.search = params.toString();
  return urlObj.toString();
}
